/**
 * HederaMonitor Page
 *
 * Main page for monitoring Hedera accounts with modern design.
 * Displays wallet balances, staking rewards, tokens, NFTs, and transaction history.
 * Inspired by FluxNodeMonitor and CronosMonitor design patterns.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useHederaWallets, useHbarPrice, useHederaTokenPrices } from '../hooks/useHederaWallet';
import { useHederaMonitorWallets } from '../hooks/useMonitorWallets';
import { isValidAccountId, hederaAPI, tinybarToHbar } from '../services/hederaApi';
import { useToast } from '../components/Toast';
import { HederaHealthMetrics } from '../components/hedera/HederaHealthMetrics';
import { HbarReleaseBanner } from '../components/hedera/HbarReleaseBanner';
import { HbarReleaseHistory } from '../components/hedera/HbarReleaseHistory';
import {
  NetworkMonitorPageHeader,
  NetworkAddressInputForm,
  NetworkMonitorEmptyState,
  MonitorTabs,
} from '../components/monitor';
import { ResponsiveTable } from '../components/ui/ResponsiveTable';
import type { HederaWalletData } from '../types/hedera.types';

// ============================================
// CONSTANTS & STYLES
// ============================================

const STORAGE_KEY = 'hedera_accounts';

const HEDERA_COLORS = {
  primary: '#8259EF', // Hedera purple
  secondary: '#6366f1', // Indigo
  accent: '#a855f7', // Purple accent
  gradient: 'linear-gradient(135deg, #8259EF, #6366f1)',
  lightGradient: 'linear-gradient(135deg, rgba(130, 89, 239, 0.15), rgba(99, 102, 241, 0.1))',
  success: '#10b981',
  warning: '#f59e0b',
};

type TabType = 'overview' | 'transactions' | 'tokens' | 'nfts' | 'rewards';

const s = {
  page: {
    padding: '0',
    maxWidth: '1400px',
    margin: '0 auto',
    paddingBottom: '40px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    gap: '20px',
    flexWrap: 'wrap' as const,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    fontSize: '36px',
    background: HEDERA_COLORS.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  titleText: {
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--muted)',
    marginTop: '4px',
  },
  card: {
    background: 'rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '20px',
    marginBottom: '20px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: (color: string) => ({
    background: `${color}10`,
    border: `1px solid ${color}30`,
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center' as const,
  }),
  statValue: {
    fontSize: '22px',
    fontWeight: 700,
  },
  statLabel: {
    fontSize: '11px',
    color: 'var(--muted)',
    marginTop: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    color: 'var(--text)',
    fontSize: '13px',
    fontFamily: 'monospace',
    outline: 'none',
  },
  button: {
    padding: '12px 20px',
    background: HEDERA_COLORS.gradient,
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  buttonSecondary: {
    padding: '12px 20px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'var(--text)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  accountItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 14px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '10px',
    marginBottom: '8px',
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '14px',
    width: 'fit-content',
    marginBottom: '24px',
  },
  tab: (isActive: boolean) => ({
    padding: '10px 18px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    background: isActive ? HEDERA_COLORS.gradient : 'transparent',
    color: isActive ? 'white' : 'var(--muted)',
    transform: isActive ? 'scale(1.02)' : 'scale(1)',
    boxShadow: isActive ? '0 4px 15px rgba(130, 89, 239, 0.3)' : 'none',
  }),
  emptyState: {
    textAlign: 'center' as const,
    padding: '60px 20px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
};

// ============================================
// SUB-COMPONENTS
// ============================================

/**
 * Network Stats Banner
 */
function NetworkStatsBanner({ hbarPrice, totalAccounts, isLoading }: {
  hbarPrice: number;
  totalAccounts: number;
  isLoading: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div style={{
      ...s.card,
      background: `linear-gradient(135deg, rgba(130, 89, 239, 0.15), rgba(99, 102, 241, 0.1))`,
      border: '1px solid rgba(130, 89, 239, 0.25)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('hedera.network.title', 'Hedera Network')}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: HEDERA_COLORS.primary }}>
              ${hbarPrice.toFixed(4)}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>HBAR</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>
              {totalAccounts}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('hedera.network.accounts', 'Accounts')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, color: HEDERA_COLORS.success }}>
              ~2.5%
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('hedera.network.stakingApy', 'Staking APY')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: isLoading ? HEDERA_COLORS.warning : HEDERA_COLORS.success,
              boxShadow: `0 0 10px ${isLoading ? HEDERA_COLORS.warning : HEDERA_COLORS.success}`,
              margin: '0 auto 4px',
            }} />
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {isLoading ? t('hedera.network.syncing', 'Syncing...') : t('hedera.network.connected', 'Connected')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Stats Overview Cards
 */
function StatsOverview({
  totalBalance,
  totalBalanceUsd,
  totalPendingRewards,
  totalPendingRewardsUsd,
  totalRewardsReceived,
  totalRewardsReceivedUsd,
  totalTokenTypes,
  totalNFTs,
}: {
  totalBalance: number;
  totalBalanceUsd: number;
  totalPendingRewards: number;
  totalPendingRewardsUsd: number;
  totalRewardsReceived: number;
  totalRewardsReceivedUsd: number;
  totalTokenTypes: number;
  totalNFTs: number;
}) {
  const { t } = useTranslation();

  const stats = [
    {
      label: t('hedera.stats.balance', 'Total Balance'),
      value: `${totalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ℏ`,
      subvalue: `$${totalBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: HEDERA_COLORS.primary,
      icon: <Icons.Wallet size={16} />,
    },
    {
      label: t('hedera.stats.pending', 'Pending Rewards'),
      value: `${totalPendingRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })} ℏ`,
      subvalue: `$${totalPendingRewardsUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: HEDERA_COLORS.warning,
      icon: <Icons.Clock size={16} />,
    },
    {
      label: t('hedera.stats.received', 'Total Rewards'),
      value: `${totalRewardsReceived.toLocaleString(undefined, { maximumFractionDigits: 2 })} ℏ`,
      subvalue: `$${totalRewardsReceivedUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: HEDERA_COLORS.success,
      icon: <Icons.TrendingUp size={16} />,
    },
    {
      label: t('hedera.stats.tokens', 'Tokens'),
      value: totalTokenTypes.toString(),
      subvalue: `${totalNFTs} NFTs`,
      color: HEDERA_COLORS.accent,
      icon: <Icons.Coins size={16} />,
    },
  ];

  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={s.statCard(stat.color)}>
          <div style={{ color: stat.color, marginBottom: '8px' }}>{stat.icon}</div>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{stat.subvalue}</div>
          <div style={s.statLabel}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Account Card Component
 */
function AccountCard({ wallet, onRemove }: { wallet: HederaWalletData; onRemove: () => void }) {
  const { t } = useTranslation();

  return (
    <div style={{
      ...s.card,
      background: 'rgba(130, 89, 239, 0.08)',
      border: '1px solid rgba(130, 89, 239, 0.2)',
      marginBottom: '12px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: HEDERA_COLORS.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <span style={{ fontSize: '18px', color: 'white' }}>ℏ</span>
          </div>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
              {wallet.accountId}
            </div>
            {wallet.stakedNodeId !== null && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                background: 'rgba(16, 185, 129, 0.15)',
                color: HEDERA_COLORS.success,
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 600,
              }}>
                <Icons.Zap size={10} />
                Staking to Node {wallet.stakedNodeId}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onRemove}
          style={{
            background: 'rgba(239, 68, 68, 0.15)',
            color: '#ef4444',
            border: 'none',
            borderRadius: '6px',
            padding: '6px 10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
          }}
        >
          <Icons.Trash2 size={12} />
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('hedera.account.balance', 'Balance')}
          </div>
          <div style={{ fontSize: '18px', fontWeight: 600 }}>
            {wallet.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ℏ
          </div>
          <div style={{ fontSize: '11px', color: HEDERA_COLORS.success }}>
            ${wallet.balanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('hedera.account.pending', 'Pending Reward')}
          </div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: HEDERA_COLORS.warning }}>
            {wallet.pendingReward.toLocaleString(undefined, { maximumFractionDigits: 4 })} ℏ
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('hedera.account.assets', 'Assets')}
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {wallet.tokens.length} {t('hedera.account.tokens', 'Tokens')}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
            {wallet.nfts.length} NFTs
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Empty State Component
 */
function EmptyState() {
  const { t } = useTranslation();

  const features = [
    { icon: <Icons.Wallet size={28} />, title: t('hedera.empty.balance', 'Balance'), desc: t('hedera.empty.balanceDesc', 'Track HBAR holdings') },
    { icon: <Icons.TrendingUp size={28} />, title: t('hedera.empty.staking', 'Staking'), desc: t('hedera.empty.stakingDesc', 'Monitor rewards') },
    { icon: <Icons.Coins size={28} />, title: t('hedera.empty.tokens', 'Tokens'), desc: t('hedera.empty.tokensDesc', 'View HTS tokens') },
    { icon: <Icons.ArrowRightLeft size={28} />, title: t('hedera.empty.history', 'History'), desc: t('hedera.empty.historyDesc', 'View transactions') },
  ];

  return (
    <NetworkMonitorEmptyState
      icon={<span style={{ fontSize: '40px', color: 'white' }}>ℏ</span>}
      title={t('hedera.empty.title', 'Welcome to Hedera Monitor')}
      description={t(
        'hedera.empty.description',
        'Track your Hedera accounts, monitor staking rewards, view token holdings, and analyze transaction history - all without connecting your wallet.',
      )}
      colors={HEDERA_COLORS}
    >
      {/* Hedera-specific feature grid below the description. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '16px',
        maxWidth: '600px',
        margin: '0 auto',
      }}>
        {features.map((feature, idx) => (
          <div key={idx} style={{
            padding: '20px 16px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}>
            <div style={{ color: HEDERA_COLORS.primary, marginBottom: '12px' }}>{feature.icon}</div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>{feature.title}</div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{feature.desc}</div>
          </div>
        ))}
      </div>
    </NetworkMonitorEmptyState>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function HederaMonitor() {
  const { t } = useTranslation();
  const toast = useToast();

  // Account management with Supabase sync
  const {
    wallets: accounts,
    setWallets: setAccounts,
    isSyncing,
  } = useHederaMonitorWallets();

  const [newAccountInput, setNewAccountInput] = useState('');
  const [showAddAccount, setShowAddAccount] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Fetch data
  const {
    wallets,
    isLoading,
    error,
    refetch,
    hbarPrice,
    totalBalance,
    totalBalanceUsd,
    totalPendingRewards,
    totalPendingRewardsUsd,
    totalRewardsReceived,
    totalRewardsReceivedUsd,
    totalTokenTypes,
    totalNFTs
  } = useHederaWallets(accounts);

  // Handle adding account
  const handleAddAccount = () => {
    const trimmedId = newAccountInput.trim();

    if (!trimmedId) {
      toast(t('hedera.toast.enterAccount', 'Please enter an account ID'), 'warning', 3000);
      return;
    }

    if (!isValidAccountId(trimmedId)) {
      toast(t('hedera.toast.invalidFormat', 'Invalid Hedera account ID format. Use format: 0.0.xxxxx'), 'error', 4000);
      return;
    }

    if (accounts.includes(trimmedId)) {
      toast(t('hedera.toast.alreadyAdded', 'This account is already added'), 'warning', 3000);
      return;
    }

    setAccounts([...accounts, trimmedId]);
    setNewAccountInput('');
    setShowAddAccount(false);
    toast(t('hedera.toast.accountAdded', `Account ${trimmedId} added successfully`), 'success', 3000);
  };

  // Handle removing account
  const handleRemoveAccount = (accountId: string) => {
    if (confirm(t('hedera.confirm.removeAccount', `Remove account ${accountId}?`))) {
      setAccounts(accounts.filter(a => a !== accountId));
      toast(t('hedera.toast.accountRemoved', 'Account removed'), 'info', 3000);
    }
  };

  // Validate input
  const isValidInput = newAccountInput.trim() && isValidAccountId(newAccountInput.trim());

  // Tabs configuration
  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('hedera.tabs.overview', 'Overview'), icon: <Icons.BarChart size={16} /> },
    { id: 'transactions', label: t('hedera.tabs.transactions', 'Transactions'), icon: <Icons.ArrowRightLeft size={16} /> },
    { id: 'tokens', label: t('hedera.tabs.tokens', 'Tokens'), icon: <Icons.Coins size={16} /> },
    { id: 'nfts', label: t('hedera.tabs.nfts', 'NFTs'), icon: <Icons.Image size={16} /> },
    { id: 'rewards', label: t('hedera.tabs.rewards', 'Rewards'), icon: <Icons.Award size={16} /> },
  ];

  // Aggregate transactions
  const allTransactions = wallets.flatMap(w =>
    w.recentTransactions.map(tx => ({ ...tx, walletId: w.accountId }))
  ).sort((a, b) => b.consensus_timestamp.localeCompare(a.consensus_timestamp));

  // Aggregate tokens
  const tokenMap = new Map<string, { symbol: string; name: string; balance: number; tokenId: string }>();
  for (const wallet of wallets) {
    for (const token of wallet.tokens) {
      const existing = tokenMap.get(token.tokenId);
      if (existing) {
        existing.balance += token.balance;
      } else {
        tokenMap.set(token.tokenId, {
          tokenId: token.tokenId,
          symbol: token.symbol,
          name: token.name,
          balance: token.balance
        });
      }
    }
  }
  const aggregatedTokens = Array.from(tokenMap.values());

  // Fetch token prices
  const tokenIds = aggregatedTokens.map(t => t.tokenId);
  const { data: tokenPrices = {} } = useHederaTokenPrices(tokenIds);

  // Aggregate NFTs
  const allNFTs = wallets.flatMap(w =>
    w.nfts.map(nft => ({ ...nft, ownerAccount: w.accountId }))
  );

  return (
    <div style={s.page}>
      <NetworkMonitorPageHeader
        icon={<span style={s.titleIcon}>ℏ</span>}
        title="Hedera Monitor"
        subtitle={t(
          'hedera.subtitle',
          'Monitor your Hedera accounts, staking rewards, tokens, and transactions',
        )}
        primaryAction={
          showAddAccount
            ? {
                label: t('common.cancel', 'Cancel'),
                icon: <Icons.X size={16} />,
                onClick: () => setShowAddAccount(false),
                variant: 'secondary',
              }
            : {
                label: t('hedera.addAccount', 'Add Account'),
                icon: <Icons.Plus size={16} />,
                onClick: () => setShowAddAccount(true),
              }
        }
        refreshAction={
          accounts.length > 0
            ? {
                label: t('common.refresh', 'Refresh'),
                icon: <Icons.RefreshCw size={16} />,
                onClick: () => refetch(),
              }
            : undefined
        }
        colors={HEDERA_COLORS}
      />

      {showAddAccount && (
        <NetworkAddressInputForm
          value={newAccountInput}
          onChange={setNewAccountInput}
          onSubmit={handleAddAccount}
          submitLabel={t('common.add', 'Add')}
          placeholder={t('hedera.placeholder', 'Enter Hedera Account ID (e.g., 0.0.12345)')}
          isValid={newAccountInput.trim() ? Boolean(isValidInput) : null}
          helperText={t(
            'hedera.hint',
            'You can find your account ID in your HashPack wallet or on HashScan',
          )}
          colors={HEDERA_COLORS}
        />
      )}

      {/* Network Stats Banner */}
      <NetworkStatsBanner
        hbarPrice={hbarPrice}
        totalAccounts={accounts.length}
        isLoading={isLoading}
      />

      {/* V2.J.1 — HBAR treasury release banner (latest detected release,
          fed by the /api/cron/check-hbar-release cron). Hides itself
          when there's no recent event so the page stays clean
          pre-release. */}
      <HbarReleaseBanner />

      {/* V2.J.3 — Full release history with mini supply chart. */}
      <HbarReleaseHistory />

      {/* Blockchain Health Metrics - Collapsible */}
      <HederaHealthMetrics />

      {/* Error Display */}
      {error && (
        <div style={{
          ...s.card,
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}>
            <Icons.AlertTriangle size={20} />
            <span>{t('hedera.error', 'Error loading data')}: {(error as Error)?.message || 'Unknown error'}</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      {accounts.length > 0 ? (
        <>
          {/* Stats Overview */}
          <StatsOverview
            totalBalance={totalBalance}
            totalBalanceUsd={totalBalanceUsd}
            totalPendingRewards={totalPendingRewards}
            totalPendingRewardsUsd={totalPendingRewardsUsd}
            totalRewardsReceived={totalRewardsReceived}
            totalRewardsReceivedUsd={totalRewardsReceivedUsd}
            totalTokenTypes={totalTokenTypes}
            totalNFTs={totalNFTs}
          />

          {/* Tabs */}
          <MonitorTabs<TabType>
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            colors={HEDERA_COLORS}
            ariaLabel="Hedera monitor tabs"
          />

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icons.Wallet size={18} />
                {t('hedera.accounts', 'Accounts')} ({wallets.length})
              </h2>
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Icons.Activity size={32} style={{ opacity: 0.3, animation: 'spin 2s linear infinite' }} />
                  <p style={{ color: 'var(--muted)', marginTop: '12px' }}>{t('hedera.loading', 'Loading...')}</p>
                </div>
              ) : (
                wallets.map(wallet => (
                  <AccountCard
                    key={wallet.accountId}
                    wallet={wallet}
                    onRemove={() => handleRemoveAccount(wallet.accountId)}
                  />
                ))
              )}
            </div>
          )}

          {activeTab === 'transactions' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('hedera.transactions.title', 'Recent Transactions')}
              </h3>
              <ResponsiveTable
                data={allTransactions.slice(0, 50)}
                keyExtractor={(tx, idx) => `${tx.transaction_id}-${idx}`}
                emptyState={
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
                    {t('hedera.transactions.empty', 'No transactions found')}
                  </div>
                }
                columns={[
                  {
                    key: 'date',
                    header: 'Date',
                    isSubtitle: true,
                    render: (tx) => {
                      const d = hederaAPI.formatTimestamp(tx.consensus_timestamp);
                      return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                    },
                  },
                  {
                    key: 'type',
                    header: 'Type',
                    isTitle: true,
                    render: (tx) => hederaAPI.getTransactionTypeName(tx.name),
                  },
                  {
                    key: 'account',
                    header: 'Account',
                    render: (tx) => (
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--muted)' }}>
                        {(tx as any).walletId || '—'}
                      </span>
                    ),
                  },
                  {
                    key: 'amount',
                    header: 'Amount',
                    align: 'right',
                    render: (tx) => {
                      const walletTransfer = tx.transfers?.find(transfer => wallets.some(w => w.accountId === transfer.account));
                      const amount = walletTransfer ? tinybarToHbar(walletTransfer.amount) : 0;
                      const isIncoming = amount > 0;
                      return (
                        <span style={{
                          fontWeight: 600,
                          color: amount === 0 ? 'var(--muted)' : isIncoming ? HEDERA_COLORS.success : '#ef4444',
                        }}>
                          {amount === 0 ? '—' : `${isIncoming ? '+' : ''}${amount.toLocaleString(undefined, { maximumFractionDigits: 4 })} ℏ`}
                        </span>
                      );
                    },
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    align: 'center',
                    render: (tx) => (
                      <span style={{
                        padding: '4px 10px',
                        fontSize: '10px',
                        fontWeight: 600,
                        borderRadius: '4px',
                        background: tx.result === 'SUCCESS' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        color: tx.result === 'SUCCESS' ? HEDERA_COLORS.success : '#ef4444',
                      }}>
                        {tx.result}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          )}

          {activeTab === 'tokens' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('hedera.tokens.title', 'Token Holdings')} ({aggregatedTokens.length})
              </h3>
              {aggregatedTokens.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
                  {aggregatedTokens.map(token => {
                    const price = tokenPrices[token.tokenId] || 0;
                    const valueUsd = price * token.balance;
                    const hasPrice = price > 0;

                    return (
                      <div key={token.tokenId} style={{
                        ...s.card,
                        marginBottom: 0,
                        background: 'rgba(168, 85, 247, 0.08)',
                        border: '1px solid rgba(168, 85, 247, 0.2)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: 600, color: HEDERA_COLORS.accent }}>
                              {token.symbol || 'Unknown'}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                              {token.name}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '16px', fontWeight: 600 }}>
                              {token.balance.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                            </div>
                            {hasPrice && (
                              <div style={{ fontSize: '12px', color: HEDERA_COLORS.success, marginTop: '2px' }}>
                                ${valueUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                          <div style={{ fontSize: '10px', color: 'var(--muted)', fontFamily: 'monospace' }}>
                            {token.tokenId}
                          </div>
                          {hasPrice && (
                            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
                              @ ${price.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ ...s.card, textAlign: 'center', color: 'var(--muted)' }}>
                  {t('hedera.tokens.empty', 'No tokens found')}
                </div>
              )}
            </div>
          )}

          {activeTab === 'nfts' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('hedera.nfts.title', 'NFT Collection')} ({allNFTs.length})
              </h3>
              {allNFTs.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                  {allNFTs.slice(0, 50).map((nft, idx) => (
                    <div key={`${nft.token_id}-${nft.serial_number}-${idx}`} style={{
                      ...s.card,
                      marginBottom: 0,
                      padding: '16px',
                    }}>
                      <div style={{
                        width: '100%',
                        aspectRatio: '1',
                        background: HEDERA_COLORS.lightGradient,
                        borderRadius: '8px',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid rgba(130, 89, 239, 0.2)',
                      }}>
                        <Icons.Image size={32} style={{ opacity: 0.4, color: HEDERA_COLORS.primary }} />
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>
                        #{nft.serial_number}
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)', fontFamily: 'monospace' }}>
                        {nft.token_id}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ ...s.card, textAlign: 'center', color: 'var(--muted)' }}>
                  {t('hedera.nfts.empty', 'No NFTs found')}
                </div>
              )}
            </div>
          )}

          {activeTab === 'rewards' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('hedera.rewards.title', 'Staking Rewards')}
              </h3>

              {/* Rewards Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={s.statCard(HEDERA_COLORS.success)}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                    {t('hedera.rewards.totalReceived', 'Total Rewards Received')}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: HEDERA_COLORS.success }}>
                    {totalRewardsReceived.toLocaleString(undefined, { maximumFractionDigits: 4 })} ℏ
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                    ${totalRewardsReceivedUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </div>
                </div>

                <div style={s.statCard(HEDERA_COLORS.warning)}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                    {t('hedera.rewards.pending', 'Pending Rewards')}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: HEDERA_COLORS.warning }}>
                    {totalPendingRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })} ℏ
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                    {t('hedera.rewards.nextPeriod', 'Next reward period')}
                  </div>
                </div>

                <div style={s.statCard(HEDERA_COLORS.primary)}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                    {t('hedera.rewards.stakingStatus', 'Staking Status')}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: HEDERA_COLORS.primary }}>
                    {wallets.filter(w => w.stakedNodeId !== null).length}/{wallets.length}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                    {t('hedera.rewards.accountsStaking', 'Accounts staking')}
                  </div>
                </div>
              </div>

              {/* Staking Details Table */}
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--muted)' }}>
                {t('hedera.rewards.detailsByAccount', 'Staking Details by Account')}
              </h4>
              <ResponsiveTable<HederaWalletData>
                data={wallets}
                keyExtractor={(w) => w.accountId}
                columns={[
                  {
                    key: 'account',
                    header: 'Account',
                    isTitle: true,
                    render: (w) => (
                      <span style={{ fontFamily: 'monospace' }}>{w.accountId}</span>
                    ),
                  },
                  {
                    key: 'balance',
                    header: 'Balance',
                    align: 'right',
                    render: (w) => `${w.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ℏ`,
                  },
                  {
                    key: 'stakedTo',
                    header: 'Staked To',
                    align: 'center',
                    render: (w) => w.stakedNodeId !== null ? (
                      <span style={{ padding: '4px 10px', background: 'rgba(16, 185, 129, 0.15)', color: HEDERA_COLORS.success, borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                        Node {w.stakedNodeId}
                      </span>
                    ) : w.stakedAccountId ? (
                      <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>{w.stakedAccountId}</span>
                    ) : (
                      <span style={{ color: 'var(--muted)', fontSize: '11px' }}>Not staking</span>
                    ),
                  },
                  {
                    key: 'pending',
                    header: 'Pending',
                    align: 'right',
                    render: (w) => (
                      <span style={{ color: HEDERA_COLORS.warning }}>
                        {w.pendingReward.toLocaleString(undefined, { maximumFractionDigits: 4 })} ℏ
                      </span>
                    ),
                  },
                  {
                    key: 'totalReceived',
                    header: 'Total Received',
                    align: 'right',
                    render: (w) => (
                      <span style={{ color: HEDERA_COLORS.success }}>
                        {w.totalRewardsReceived.toLocaleString(undefined, { maximumFractionDigits: 4 })} ℏ
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </>
      ) : (
        <EmptyState />
      )}

      {/* Info Footer */}
      <div style={{
        marginTop: '30px',
        padding: '16px 20px',
        background: 'rgba(130, 89, 239, 0.08)',
        borderRadius: '12px',
        border: '1px solid rgba(130, 89, 239, 0.15)',
        fontSize: '12px',
        color: 'var(--muted)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Icons.Info size={14} style={{ color: HEDERA_COLORS.primary }} />
          <strong style={{ color: 'var(--text)' }}>{t('hedera.info.title', 'About Hedera Monitor')}</strong>
        </div>
        <p style={{ lineHeight: 1.6 }}>
          {t('hedera.info.description', 'This page uses the Hedera Mirror Node public API to fetch account data. No wallet connection is required - just enter your account ID to start monitoring. Data is refreshed every 5 minutes automatically.')}
        </p>
      </div>
    </div>
  );
}
